// src/pages/AFSHistory.jsx
// Firm-wide AFS history — all completed jobs across all clients
// v2.6: Sign-off status badges added to each job card
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FileText } from 'lucide-react'
import { getAFSJobs, downloadAFSJob, downloadAFSJobWord, resumeJobProgress, downloadReviewReportPdf, downloadXBRLJob,
         downloadEngagementLetter, downloadRepresentationLetter, discardDraftJob } from '../api/kailux'
import EmptyState from '../components/EmptyState'
import BackButton from '../components/BackButton'
import { STAGE_TO_ROUTE, STAGE_LABEL } from '../constants/afsWorkflow'


import { T, alpha } from '../theme/theme'
import Icon from '../components/Icon'
const STATUS_CONFIG = {
  complete: { label:'Complete',  color:T.gn, bg:T.gnd },
  review:   { label:'In Review', color:T.bl, bg:T.bld },
  mapping:  { label:'Mapping',   color:T.yl, bg:T.yld },
  upload:   { label:'Uploading', color:T.mt, bg:alpha(T.tx, 0.04) },
  draft:    { label:'Draft',     color:T.yl, bg:T.yld },
  default:  { label:'Unknown',   color:T.mt, bg:alpha(T.tx, 0.04) },
}

// Phase 5 (Issue A) — human-readable label for the saved workflow_stage,
// shown alongside the status badge on in-progress cards so "Draft" cards
// tell the user roughly how far along they got. (STAGE_LABEL itself now
// lives in ../constants/afsWorkflow, shared with Dashboard.jsx.)

const SIGNOFF_CONFIG = {
  signed:  { label:'Signed',          icon:'check',     color:T.gn, bg:T.gnd },
  pending: { label:'Awaiting Sign-off', icon:'hourglass', color:T.yl, bg:T.yld },
}

const ENG_LABELS = {
  compilation: 'Compilation',
  review:      'Review',
  audit:       'Audit',
  accounting_officer: "Accounting Officer's Report",
  in_house:    'Prepared in-house',
}

function triggerDownload(blob, filename) {
  const url  = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href  = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default function AFSHistory() {
  const [jobs,        setJobs]        = useState([])
  const [loading,     setLoading]     = useState(true)
  const [downloading, setDownloading] = useState(null)
  const [dlError,     setDlError]     = useState(null)
  const [resuming,    setResuming]    = useState(null)
  const [resumeError, setResumeError] = useState(null)
  const [dlKind,       setDlKind]     = useState(null) // `${jobId}:review` | `${jobId}:xbrl`
  const [discarding,    setDiscarding]    = useState(null)
  const [discardError,  setDiscardError]  = useState(null)
  const [search,      setSearch]      = useState('')
  const [filterYear,  setFilterYear]  = useState('all')
  const [filterEng,   setFilterEng]   = useState('all')
  const [filterSignoff, setFilterSignoff] = useState('all')

  const { firm }  = useAuth()
  const navigate  = useNavigate()

  useEffect(() => {
    if (!firm?.id) return
    getAFSJobs(firm.id)
      .then(({ data }) => setJobs(data || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [firm?.id])

  const handleDownload = async (job) => {
    setDownloading(job.id)
    setDlError(null)
    try {
      const { data } = await downloadAFSJob(job.id)
      const clientName = (job.clients?.name || 'AFS').replace(/ /g,'_').replace(/\//g,'-')
      triggerDownload(data, `${clientName}_AFS_${job.financial_year}.pdf`)
    } catch (err) {
      setDlError(job.id)
      setTimeout(() => setDlError(null), 3000)
    } finally {
      setDownloading(null)
    }
  }

  // Phase 5 (Issue A) — resume a job that was started but never finished.
  // Fetches the saved step-boundary state and sends the preparer back to
  // whichever page they left off on, with that state rehydrated, instead of
  // "New AFS" silently restarting from a blank Trial Balance page.
  const handleResume = async (job) => {
    setResuming(job.id)
    setResumeError(null)
    try {
      const { data } = await resumeJobProgress(job.id)
      const route = STAGE_TO_ROUTE[data.workflow_stage] || '/trial-balance'
      const clientName = job.clients?.name || ''
      navigate(route, {
        state: {
          client: { id: data.client_id, name: clientName },
          afsJobId: data.job_id,
          mappings: data.mappings, accounts: data.accounts, figures: data.figures,
          prior_figures: data.prior_figures, first_year: data.first_year,
          prior_not_available: data.prior_not_available,
          financial_year: data.financial_year, engagement_type: data.engagement_type,
          notes: data.notes_override,
          // FIX: forward the previously-confirmed disclosure checklist so
          // Review.jsx can restore it instead of forcing a full re-tick on
          // every resume — see kailux-fixes-log.md.
          disclosure_checklist: data.disclosure_checklist,
          // Draft-autosave fix: bank-statement-path resume data (uploaded
          // transactions, categorizations, flag decisions, manual balance
          // sheet entries) lives in its own blob — see BankStatement.jsx /
          // BankStatementBalanceSheet.jsx and kailux-fixes-log.md.
          bank_statement_data: data.bank_statement_data,
        },
      })
    } catch (err) {
      setResumeError(job.id)
      setTimeout(() => setResumeError(null), 3000)
    } finally {
      setResuming(null)
    }
  }

  // Phase 5 (Issue B) — redownload outputs generated alongside the AFS
  // that previously were only ever available once, at generation time.
  const EXTRA_DOWNLOADS = {
    word:           { fn: downloadAFSJobWord,             ext: 'docx', suffix: 'AFS_Word' },
    review:         { fn: downloadReviewReportPdf,        ext: 'pdf',  suffix: 'Expert_Review' },
    xbrl:           { fn: downloadXBRLJob,                ext: 'html', suffix: 'iXBRL' },
    engagement:     { fn: downloadEngagementLetter,       ext: 'pdf',  suffix: 'Engagement_Letter' },
    representation: { fn: downloadRepresentationLetter,   ext: 'pdf',  suffix: 'Representation_Letter' },
  }

  const handleDownloadExtra = async (job, kind) => {
    const cfg = EXTRA_DOWNLOADS[kind]
    setDlKind(`${job.id}:${kind}`)
    setDlError(null)
    try {
      const { data } = await cfg.fn(job.id)
      const clientName = (job.clients?.name || 'AFS').replace(/ /g,'_').replace(/\//g,'-')
      triggerDownload(data, `${clientName}_${cfg.suffix}_${job.financial_year}.${cfg.ext}`)
    } catch (err) {
      setDlError(`${job.id}:${kind}`)
      setTimeout(() => setDlError(null), 3000)
    } finally {
      setDlKind(null)
    }
  }

  // Phase 5 (Issue A) — explicitly abandon an in-progress draft. AFS History
  // previously had no way to remove a started-but-never-finished job; it
  // would just sit there forever once Resume existed.
  const handleDiscard = async (job) => {
    if (!window.confirm(
      `Discard this in-progress AFS for ${job.clients?.name || 'this client'} ` +
      `(${job.financial_year})? This cannot be undone — you'll need to start over from scratch.`
    )) return
    setDiscarding(job.id)
    setDiscardError(null)
    try {
      await discardDraftJob(job.id)
      setJobs(prev => prev.filter(j => j.id !== job.id))
    } catch (err) {
      setDiscardError(job.id)
      setTimeout(() => setDiscardError(null), 3000)
    } finally {
      setDiscarding(null)
    }
  }

  // Derive filter options from data
  const years       = [...new Set(jobs.map(j => j.financial_year).filter(Boolean))].sort((a,b) => b-a)
  const engagements = [...new Set(jobs.map(j => j.engagement_type).filter(Boolean))]

  const filtered = jobs.filter(j => {
    const name        = (j.clients?.name || '').toLowerCase()
    const matchSearch = !search || name.includes(search.toLowerCase()) || (j.financial_year || '').includes(search)
    const matchYear   = filterYear    === 'all' || j.financial_year  === filterYear
    const matchEng    = filterEng     === 'all' || j.engagement_type === filterEng
    const matchSig    = filterSignoff === 'all'
      || (filterSignoff === 'signed'   && j.signoff_status === 'signed')
      || (filterSignoff === 'pending'  && j.signoff_status === 'pending')
      || (filterSignoff === 'unsigned' && !j.signoff_status)
    return matchSearch && matchYear && matchEng && matchSig
  })

  const complete   = jobs.filter(j => j.status === 'complete').length
  const signed     = jobs.filter(j => j.signoff_status === 'signed').length
  const inProgress = jobs.filter(j => j.status !== 'complete').length

  // Group by financial year for the timeline view
  const byYear = {}
  filtered.forEach(j => {
    const yr = j.financial_year || 'Unknown'
    if (!byYear[yr]) byYear[yr] = []
    byYear[yr].push(j)
  })
  const sortedYears = Object.keys(byYear).sort((a,b) => b.localeCompare(a))

  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>

      {/* ── NAV ── */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 32px', height:'62px', display:'flex',
        alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          <BackButton onClick={() => navigate('/dashboard')} label="Dashboard"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
            Kai<span style={{ color:T.or }}>Lux</span>
          </div>
          <span style={{ fontSize:'14px', color:T.mt }}>/ AFS History</span>
        </div>
        <button onClick={() => navigate('/clients')}
          style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
            background:T.prg, color:T.onpri,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px',
            fontWeight:'700', cursor:'pointer' }}>
          + New AFS
        </button>
      </div>

      <div style={{ padding:'32px' }}>

        {/* ── HEADER ── */}
        <div style={{ marginBottom:'28px' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px',
            fontWeight:'800', letterSpacing:'-0.5px', marginBottom:'6px' }}>
            AFS History
          </div>
          <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt }}>
            All financial statements generated by your firm
          </div>
        </div>

        {/* ── STATS ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)',
          gap:'12px', marginBottom:'24px' }}>
          {[
            { label:'Total Generated', value:jobs.length,  color:T.or },
            { label:'Completed',       value:complete,      color:T.gn },
            { label:'Director Signed', value:signed,        color:T.bl },
            { label:'In Progress',     value:inProgress,    color:T.yl },
          ].map((s,i) => (
            <div key={i} style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'18px 20px' }}>
              <div style={{ fontSize:'9px', color:T.mt, letterSpacing:'1.5px',
                fontWeight:'600', marginBottom:'8px' }}>
                {s.label.toUpperCase()}
              </div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'30px',
                fontWeight:'800', color:s.color, letterSpacing:'-1px' }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* ── FILTERS ── */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'20px',
          flexWrap:'wrap', alignItems:'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by client or year..."
            style={{ padding:'9px 14px', background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'8px', color:T.tx, fontSize:'14px',
              fontFamily:"'Outfit',sans-serif", outline:'none', width:'220px' }}
            onFocus={e => e.target.style.borderColor = T.or}
            onBlur={e  => e.target.style.borderColor = T.bd}/>

          {[
            { value:filterYear,    setter:setFilterYear,    options:[['all','All years'], ...years.map(y=>[y,y])],                 label:'year'       },
            { value:filterEng,     setter:setFilterEng,     options:[['all','All engagements'], ...engagements.map(e=>[e,ENG_LABELS[e]||e])], label:'engagement' },
            { value:filterSignoff, setter:setFilterSignoff, options:[['all','All sign-off status'],['signed','Signed'],['pending','Awaiting'],['unsigned','Not sent']], label:'signoff' },
          ].map(({ value, setter, options, label }) => (
            <select key={label} value={value} onChange={e => setter(e.target.value)}
              style={{ padding:'9px 14px', background:T.s1, border:`1px solid ${T.bd}`,
                borderRadius:'8px', color: value==='all' ? T.mt : T.tx,
                fontSize:'14px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
              {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}

          {(search || filterYear !== 'all' || filterEng !== 'all' || filterSignoff !== 'all') && (
            <button onClick={() => {
              setSearch(''); setFilterYear('all');
              setFilterEng('all'); setFilterSignoff('all')
            }}
              style={{ padding:'9px 14px', borderRadius:'8px',
                border:`1px solid ${T.bd}`, background:'transparent',
                color:T.mt, fontFamily:"'Outfit',sans-serif",
                fontSize:'14px', cursor:'pointer' }}>
              Clear filters
            </button>
          )}

          <span style={{ marginLeft:'auto', fontSize:'13px', color:T.mt }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── CONTENT ── */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'80px', color:T.mt, fontSize:'14px' }}>
            Loading AFS history...
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            size="large"
            icon={FileText}
            title={jobs.length === 0 ? 'No AFS generated yet' : 'No results match your filters'}
            subtitle={jobs.length === 0
              ? 'Start by uploading a trial balance for a client'
              : 'Try adjusting your search or filters'}
            action={jobs.length === 0 && (
              <button onClick={() => navigate('/clients')}
                style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor:'pointer' }}>
                Go to Clients →
              </button>
            )}
          />
        ) : (
          sortedYears.map(year => (
            <div key={year} style={{ marginBottom:'32px' }}>
              {/* Year header */}
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px' }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'15px',
                  fontWeight:'800', color:T.or }}>{year}</div>
                <div style={{ flex:1, height:'1px', background:T.bd }}/>
                <span style={{ fontSize:'12px', color:T.mt }}>
                  {byYear[year].length} AFS
                </span>
              </div>

              {/* Job cards */}
              <div style={{ display:'grid',
                gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:'12px' }}>
                {byYear[year].map(job => {
                  const sc          = STATUS_CONFIG[job.status] || STATUS_CONFIG.default
                  const sigCfg      = SIGNOFF_CONFIG[job.signoff_status]
                  const eng         = ENG_LABELS[job.engagement_type] || job.engagement_type || '—'
                  const clientName  = job.clients?.name || '—'
                  const createdDate = job.created_at
                    ? new Date(job.created_at).toLocaleDateString('en-ZA',
                        { day:'numeric', month:'short', year:'numeric' })
                    : '—'

                  // Key figures
                  const fig = job.figures || {}
                  const revenue = fig['Revenue'] || 0
                  const pat = (() => {
                    const r    = fig['Revenue'] || 0
                    const cogs = fig['Cost of Sales'] || 0
                    const opex = ['Employee Costs','Occupancy Costs','Communication Expenses',
                      'Travel and Motor Expenses','General and Admin Expenses','Professional Fees',
                      'Depreciation and Amortisation','Credit Losses','Sales and Marketing']
                      .reduce((s,k) => s + (fig[k] || 0), 0)
                    const fc  = fig['Finance Costs'] || 0
                    const tax = fig['Income Tax Expense'] || 0
                    return (r - cogs) - opex - fc - tax + (fig['Other Income'] || 0)
                  })()
                  const assets = (() => {
                    const ppe = Math.max(
                      (fig['Property Plant & Equipment'] || 0) - (fig['Accumulated Depreciation'] || 0), 0)
                    return ppe + (fig['Intangible Assets'] || 0) + (fig['Investments'] || 0) + (fig['Deferred Tax Asset'] || 0) +
                      (fig['Inventories'] || 0) + (fig['Trade and Other Receivables'] || 0) +
                      (fig['Tax Receivables'] || 0) + (fig['Cash and Cash Equivalents'] || 0)
                  })()

                  const hasFigures    = Object.keys(fig).length > 0
                  const isDownloading = downloading === job.id
                  const hasError      = dlError === job.id
                  const isResuming    = resuming === job.id
                  const hasResumeError = resumeError === job.id

                  return (
                    <div key={job.id}
                      style={{ background:T.s1, border:`1px solid ${T.bd}`,
                        borderRadius:'12px', overflow:'hidden', transition:'all 0.2s' }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = alpha(T.or, 0.31)
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = T.bd
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}>

                      {/* Card top */}
                      <div style={{ padding:'18px 20px 14px' }}>
                        {/* Client + badges row */}
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'flex-start', marginBottom:'10px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'36px', height:'36px', borderRadius:'8px',
                              background:T.ord, border:`1px solid ${alpha(T.or, 0.19)}`, flexShrink:0,
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:'14px', fontWeight:'800', color:T.or }}>
                              {clientName[0] || '?'}
                            </div>
                            <div>
                              <div style={{ fontSize:'14px', fontWeight:'700',
                                color:T.tx, lineHeight:'1.3' }}>{clientName}</div>
                              <div style={{ fontSize:'10px', color:T.mt, marginTop:'1px' }}>
                                {eng} · {createdDate}
                              </div>
                            </div>
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:'4px', alignItems:'flex-end' }}>
                            <span style={{ fontSize:'9px', fontWeight:'700',
                              padding:'3px 8px', borderRadius:'6px',
                              background:sc.bg, color:sc.color }}>
                              {sc.label.toUpperCase()}
                            </span>
                            {job.status !== 'complete' && STAGE_LABEL[job.workflow_stage] && (
                              <span style={{ fontSize:'9px', color:T.mt }}>
                                Left at: {STAGE_LABEL[job.workflow_stage]}
                              </span>
                            )}
                            {sigCfg && (
                              <span style={{ fontSize:'9px', fontWeight:'700',
                                padding:'3px 8px', borderRadius:'6px',
                                background:sigCfg.bg, color:sigCfg.color }}>
                                {sigCfg.icon && <><Icon name={sigCfg.icon} />{' '}</>}{sigCfg.label.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Key figures */}
                        {hasFigures ? (
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr',
                            gap:'8px', marginBottom:'14px' }}>
                            {[
                              { label:'Revenue',      value:revenue },
                              { label:'Profit',       value:pat     },
                              { label:'Total Assets', value:assets  },
                            ].map((kf,ki) => (
                              <div key={ki} style={{ background:T.s2,
                                border:`1px solid ${T.bd}`, borderRadius:'8px',
                                padding:'8px 10px' }}>
                                <div style={{ fontSize:'9px', color:T.mt,
                                  letterSpacing:'0.5px', marginBottom:'4px' }}>
                                  {kf.label.toUpperCase()}
                                </div>
                                <div style={{ fontSize:'12px', fontWeight:'700',
                                  color: kf.label==='Profit'
                                    ? (kf.value >= 0 ? T.gn : T.rd) : T.tx }}>
                                  {kf.value === 0 ? '—'
                                    : kf.value < 0
                                    ? `(R${Math.abs(kf.value).toLocaleString('en-ZA',{maximumFractionDigits:0})})`
                                    : `R${kf.value.toLocaleString('en-ZA',{maximumFractionDigits:0})}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ padding:'10px 12px', background:T.s2,
                            borderRadius:'8px', border:`1px solid ${T.bd}`,
                            fontSize:'12px', color:T.mt, marginBottom:'14px' }}>
                            No figures stored for this job
                          </div>
                        )}
                      </div>

                      {/* Card footer */}
                      <div style={{ borderTop:`1px solid ${T.bd}`,
                        padding:'12px 20px', display:'flex',
                        alignItems:'center', justifyContent:'space-between', gap:'8px' }}>

                        {job.download_count > 0 && (
                          <div title={job.last_downloaded_at
                              ? `Last downloaded ${new Date(job.last_downloaded_at).toLocaleString()}`
                              : undefined}
                            style={{ fontSize:'11px', color:T.mt, whiteSpace:'nowrap' }}>
                            ↓ {job.download_count} download{job.download_count !== 1 ? 's' : ''}
                          </div>
                        )}

                        <button
                          onClick={() => navigate('/trial-balance', {
                            state:{ client:{ id:job.client_id, name:clientName } }
                          })}
                          style={{ padding:'7px 14px', borderRadius:'6px',
                            border:`1px solid ${T.bd}`, background:'transparent',
                            color:T.mt, fontFamily:"'Outfit',sans-serif",
                            fontSize:'12px', fontWeight:'600', cursor:'pointer',
                            transition:'all 0.2s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor=T.or; e.currentTarget.style.color=T.or }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.color=T.mt }}>
                          New AFS
                        </button>

                        {job.status === 'complete' && (
                          <>
                          <button
                            onClick={() => navigate('/statements', {
                              state:{
                                client:{ id:job.client_id, name:clientName },
                                afsJobId: job.id,
                                financial_year: job.financial_year,
                                figures: job.figures || {},
                                prior_figures: job.prior_figures || {},
                              }
                            })}
                            style={{ padding:'7px 14px', borderRadius:'6px',
                              border:`1px solid ${T.bd}`, background:'transparent',
                              color:T.mt, fontFamily:"'Outfit',sans-serif",
                              fontSize:'12px', fontWeight:'600', cursor:'pointer',
                              transition:'all 0.2s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor=T.or; e.currentTarget.style.color=T.or }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.color=T.mt }}>
                            View
                          </button>

                          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', justifyContent:'flex-end' }}>
                            {[
                              ['word',           job.docx_path,                  'Redownload the AFS as an editable Word document',               '↓ Word (.docx)'],
                              ['review',         job.review_report,              'Download the Expert Review ratios & AI observations snapshot', '↓ Review Report'],
                              ['xbrl',           job.xbrl_html,                   'Redownload the CIPC iXBRL submission file',                      '↓ iXBRL (CIPC)'],
                              ['engagement',     job.engagement_letter_path,      'Redownload the engagement letter',                               '↓ Engagement Letter'],
                              ['representation', job.representation_letter_path, 'Redownload the management representation letter',                '↓ Rep. Letter'],
                            ].map(([kind, present, title, label]) => present && (
                              <button key={kind}
                                onClick={() => handleDownloadExtra(job, kind)}
                                disabled={dlKind === `${job.id}:${kind}`}
                                title={title}
                                style={{ padding:'7px 12px', borderRadius:'6px',
                                  border:`1px solid ${dlError === `${job.id}:${kind}` ? T.rd : T.bd}`,
                                  background:'transparent',
                                  color: dlError === `${job.id}:${kind}` ? T.rd : T.mt,
                                  fontFamily:"'Outfit',sans-serif", fontSize:'11px',
                                  fontWeight:'600',
                                  cursor: dlKind === `${job.id}:${kind}` ? 'not-allowed' : 'pointer' }}>
                                {dlKind === `${job.id}:${kind}` ? '…' :
                                  dlError === `${job.id}:${kind}` ? <><Icon name="alert" /> Retry</> : label}
                              </button>
                            ))}
                            <button
                              onClick={() => handleDownload(job)}
                              disabled={isDownloading}
                              style={{ padding:'7px 16px', borderRadius:'6px', border:'none',
                                background: hasError ? alpha(T.rd, 0.15) : T.prg,
                                opacity: isDownloading ? 0.6 : 1,
                                color: hasError ? T.rd : T.onpri,
                                fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                                fontWeight:'700',
                                cursor: isDownloading ? 'not-allowed' : 'pointer',
                                display:'flex', alignItems:'center', gap:'4px',
                                transition:'all 0.2s' }}>
                              {isDownloading ? (
                                <>
                                  <span style={{ width:'10px', height:'10px',
                                    border:`2px solid ${alpha(T.onpri, 0.3)}`,
                                    borderTopColor:T.onpri, borderRadius:'50%',
                                    animation:'spin 0.8s linear infinite',
                                    display:'inline-block' }}/>
                                  Downloading...
                                </>
                              ) : hasError ? <><Icon name="alert" /> Failed — retry</> : '↓ Download PDF'}
                            </button>
                          </div>
                          </>
                        )}
                        {job.status !== 'complete' && (
                          <div style={{ display:'flex', gap:'8px' }}>
                            <button
                              onClick={() => handleDiscard(job)}
                              disabled={discarding === job.id}
                              title="Abandon this draft — cannot be undone"
                              style={{ padding:'7px 14px', borderRadius:'6px',
                                border:`1px solid ${discardError === job.id ? T.rd : T.bd}`,
                                background:'transparent',
                                color: discardError === job.id ? T.rd : T.mt,
                                fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'600',
                                cursor: discarding === job.id ? 'not-allowed' : 'pointer' }}>
                              {discarding === job.id ? 'Discarding...' :
                                discardError === job.id ? <><Icon name="alert" /> Failed — retry</> : 'Discard'}
                            </button>
                            <button
                              onClick={() => handleResume(job)}
                              disabled={isResuming}
                              style={{ padding:'7px 16px', borderRadius:'6px', border:'none',
                                background: hasResumeError ? alpha(T.rd, 0.15) : T.bl,
                                opacity: isResuming ? 0.6 : 1,
                              color: hasResumeError ? T.rd : T.onpri,
                              fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                              fontWeight:'700',
                              cursor: isResuming ? 'not-allowed' : 'pointer' }}>
                            {isResuming ? 'Resuming...' : hasResumeError ? <><Icon name="alert" /> Failed — retry</> : '→ Resume'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
